import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as jobFairAPI from '../../utils/api/jobFair.js';
import { getErrorMessage } from '../../utils/api/errors.js';

export const fetchJobFairLinks = createAsyncThunk('jobFair/fetchLinks', async (_, { rejectWithValue }) => {
  try {
    return await jobFairAPI.getJobFairLinks();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch job fair links'));
  }
});

export const createJobFairLinkThunk = createAsyncThunk('jobFair/createLink', async (payload, { rejectWithValue }) => {
  try {
    return await jobFairAPI.createJobFairLink(payload);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to create job fair link'));
  }
});

export const deleteJobFairLinkThunk = createAsyncThunk('jobFair/deleteLink', async (id, { rejectWithValue }) => {
  try {
    await jobFairAPI.deleteJobFairLink(id);
    return id;
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to delete job fair link'));
  }
});

export const fetchJobFairLeads = createAsyncThunk('jobFair/fetchLeads', async (_, { rejectWithValue }) => {
  try {
    return await jobFairAPI.getJobFairLeads();
  } catch (error) {
    return rejectWithValue(getErrorMessage(error, 'Failed to fetch job fair leads'));
  }
});

export const convertLeadToCandidateThunk = createAsyncThunk(
  'jobFair/convertLead',
  async (leadId, { rejectWithValue }) => {
    try {
      return await jobFairAPI.convertJobFairLead(leadId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to convert lead'));
    }
  }
);

const initialState = {
  links: [],
  leads: [],
  loading: false,
  leadsLoading: false,
  error: null
};

const jobFairSlice = createSlice({
  name: 'jobFair',
  initialState,
  reducers: {
    clearJobFairError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobFairLinks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobFairLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.links = action.payload || [];
      })
      .addCase(fetchJobFairLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createJobFairLinkThunk.fulfilled, (state, action) => {
        state.links.unshift(action.payload);
      })
      .addCase(createJobFairLinkThunk.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteJobFairLinkThunk.fulfilled, (state, action) => {
        state.links = state.links.filter((link) => link.id !== action.payload);
      })
      .addCase(deleteJobFairLinkThunk.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchJobFairLeads.pending, (state) => {
        state.leadsLoading = true;
      })
      .addCase(fetchJobFairLeads.fulfilled, (state, action) => {
        state.leadsLoading = false;
        state.leads = action.payload || [];
      })
      .addCase(fetchJobFairLeads.rejected, (state, action) => {
        state.leadsLoading = false;
        state.error = action.payload;
      })
      .addCase(convertLeadToCandidateThunk.fulfilled, (state, action) => {
        const idx = state.leads.findIndex((l) => l.id === action.payload.id);
        if (idx !== -1) state.leads[idx] = action.payload;
      })
      .addCase(convertLeadToCandidateThunk.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const { clearJobFairError } = jobFairSlice.actions;
export default jobFairSlice.reducer;
